/**
 * Live EC for an SDI-12 `sensor_registry` doc, on the pore-water basis
 * (LOCKED: `.cursor/rules/ec-basis-pore.mdc`).
 *
 * sdi12Service writes `ec` / `bulkEc` = the probe's BULK EC (telemetry) and
 * `poreEc` = Hilhorst pore-water EC, the steering basis — null when the frame
 * cannot be converted (probe dry or in air, `poreEcConfidence: 'invalid'`).
 * Docs written before 2026-09-03 have no `poreEc` field and carry bulk EC only.
 */

export type RegistryPoreEcConfidence = 'ok' | 'low' | 'invalid';

export interface RegistrySoilEc {
  /** Pore-water EC (dS/m). Bulk only when `ecBasis` is 'bulk'. Null when unavailable. */
  ec: number | null;
  /** Probe wire EC (bulk, dS/m) — telemetry only; never compare to a target. */
  bulkEc: number | null;
  ecBasis: 'pore' | 'bulk';
  poreEcConfidence: RegistryPoreEcConfidence | null;
}

function finite(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

export function registrySoilEc(data: Record<string, unknown>): RegistrySoilEc {
  const bulkEc = finite(data.bulkEc) ?? finite(data.ec);
  const confidence = data.poreEcConfidence;
  const poreEcConfidence: RegistryPoreEcConfidence | null =
    confidence === 'ok' || confidence === 'low' || confidence === 'invalid' ? confidence : null;

  if (!('poreEc' in data)) {
    return { ec: bulkEc, bulkEc, ecBasis: 'bulk', poreEcConfidence };
  }
  const poreEc = poreEcConfidence === 'invalid' ? null : finite(data.poreEc);
  return { ec: poreEc, bulkEc, ecBasis: 'pore', poreEcConfidence };
}

/** Hover text for an EC value from {@link registrySoilEc}. */
export function registrySoilEcTitle(r: RegistrySoilEc): string {
  const bulk = r.bulkEc != null ? ` · bulk ${r.bulkEc.toFixed(2)} dS/m` : '';
  if (r.ecBasis === 'bulk') return 'Bulk EC from the probe (pore EC not yet published)';
  if (r.ec == null) return `Pore-water EC unavailable: substrate too dry to convert${bulk}`;
  const low = r.poreEcConfidence === 'low' ? ' · low confidence' : '';
  return `Pore-water EC (Hilhorst)${low}${bulk}`;
}
